"use client";

import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";

const POPUP_WIDTH = 560;
const POPUP_HEIGHT = 680;

function GoogleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z"
        fill="#EA4335"
      />
    </svg>
  );
}

interface GoogleSignInButtonProps {
  returnTo?: string;
  returnHost?: string;
}

export function GoogleSignInButton({
  returnTo,
  returnHost,
}: GoogleSignInButtonProps) {
  const popupRef = useRef<Window | null>(null);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "auth-popup") return;

      const { status, returnTo: rt, error } = event.data;

      if (status === "success") {
        // rt is always a same-origin path (an app route, or /api/auth/continue
        // for cross-host admin deep links). Guard against anything else.
        window.location.href =
          typeof rt === "string" && rt.startsWith("/") ? rt : "/";
      } else if (status === "org-selection") {
        // Cookie is set — reload so the server picks it up and shows org list.
        window.location.reload();
      } else {
        const errorParam = error ? `?error=${error}` : "";
        window.location.href = `/login${errorParam}`;
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  function buildLoginHref(popup: boolean): string {
    const params = new URLSearchParams();
    if (popup) params.set("popup", "1");
    params.set("returnTo", returnTo ?? "/");
    if (returnHost) params.set("returnHost", returnHost);
    return `/api/auth/login?${params.toString()}`;
  }

  function openPopup() {
    const href = buildLoginHref(true);

    const x = Math.max(0, (window.screen.width - POPUP_WIDTH) / 2);
    const y = Math.max(0, (window.screen.height - POPUP_HEIGHT) / 2);

    const popup = window.open(
      href,
      "google-auth",
      `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${x},top=${y},scrollbars=yes,resizable=yes`
    );

    if (!popup) {
      // Popup blocked — fall back to full-page redirect.
      window.location.href = buildLoginHref(false);
      return;
    }

    popupRef.current = popup;
  }

  return (
    <Button
      variant="outline"
      size="lg"
      className="w-full max-w-xs gap-3 font-medium"
      onClick={openPopup}
    >
      <GoogleIcon />
      Sign in with Google
    </Button>
  );
}
